/**
 * InputArea - Message input for the Travel Agent chat
 *
 * Features:
 * - Textarea with auto-expand up to 4 rows
 * - Enter to send, Shift+Enter for new line
 * - Send button with loading spinner while agent responds
 * - Disabled when no session, no trip, or agent is responding
 */

import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, IconButton, CircularProgress, Tooltip } from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';

/**
 * InputArea Component
 *
 * @param {Object} props
 * @param {Function} props.onSendMessage - Called with (text) when user sends
 * @param {boolean} [props.disabled=false] - Whether input is disabled
 * @param {boolean} [props.isResponding=false] - Whether agent is responding
 * @param {string} [props.placeholder] - Input placeholder text
 */
const InputArea = ({
  onSendMessage,
  disabled = false,
  isResponding = false,
  placeholder = 'Ask your travel assistant anything...'
}) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);

  // Auto-focus the input whenever it becomes enabled (e.g. after a new session is created)
  useEffect(() => {
    if (!disabled) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [disabled]);

  const canSend = inputValue.trim().length > 0 && !disabled && !isResponding;

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = async () => {
    if (!canSend) return;

    const text = inputValue.trim();
    setInputValue('');

    try {
      await onSendMessage(text);
    } catch (error) {
      console.error('[InputArea] Error sending message:', error);
      // Restore input value on failure
      setInputValue(text);
    }

    // Re-focus the input after sending
    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
  };

  return (
    <Box
      sx={{
        padding: '12px 16px',
        borderTop: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        display: 'flex',
        gap: 1,
        alignItems: 'flex-end',
        flexShrink: 0
      }}
    >
      <TextField
        inputRef={inputRef}
        multiline
        maxRows={4}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={
          disabled
            ? 'Select a trip to use the Travel Agent'
            : isResponding
            ? 'Agent is responding...'
            : placeholder
        }
        disabled={disabled || isResponding}
        fullWidth
        variant="outlined"
        size="small"
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'background.paper'
          },
          '& .MuiOutlinedInput-input': {
            fontSize: '0.9375rem'
          }
        }}
      />

      <Tooltip title={canSend ? 'Send message' : isResponding ? 'Agent is responding...' : 'Type a message'}>
        <span>
          <IconButton
            onClick={handleSend}
            disabled={!canSend}
            color="primary"
            aria-label="Send message"
            sx={{
              mb: '2px',
              flexShrink: 0
            }}
          >
            {isResponding ? (
              <CircularProgress size={22} />
            ) : (
              <SendIcon />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default InputArea;
